import React, { useState, useEffect, useCallback } from 'react';
import {
  getAdminConfig, updateAdminConfig,
  getTools, updateTool, deleteTool, testTool,
  AiTool,
} from 'core/services/admin.service';
import { AD } from './admin.constants';
import { s } from './admin.styles';
import { PromptRecord, ToolRecord, ToastState } from './admin.types';
import { mapAiToolToToolRecord } from './admin.utils';
import { ADIcon } from './ADIcon';
import { StatusBadge } from './StatusBadge';
import { AdminToast } from './AdminToast';
import { SidebarItem } from './SidebarItem';
import { PromptEditor } from './PromptEditor';
import { ToolEditor } from './ToolEditor';
import { PlaygroundPanel } from './PlaygroundPanel';
import { ToolStatsPanel } from './ToolStatsPanel';
import { CreateToolForm } from './CreateToolForm';

/**
 * AdminPage — 3-pane IDE-like workspace for managing AI prompts and tools.
 * Left rail: combined library list. Middle: editor. Right: playground + history.
 */
export function AdminPage() {
  const [kind, setKind] = useState<'prompt' | 'tool'>('prompt');
  const [rightTab, setRightTab] = useState<'playground' | 'history'>('playground');
  const [activeFilter, setActiveFilter] = useState<'all' | 'drafts' | 'deprecated'>('all');
  const [toast, setToast] = useState<ToastState>({ visible: false, message: '', isError: false });
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [prompts, setPrompts] = useState<PromptRecord[]>([]);
  const [tools, setTools] = useState<ToolRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [creating, setCreating] = useState(false);

  // Playground state
  const [playInput, setPlayInput] = useState('');
  const [playOutput, setPlayOutput] = useState('');
  const [playStatus, setPlayStatus] = useState<{ code: number; ms: number } | null>(null);
  const [playRunning, setPlayRunning] = useState(false);

  // Load real config from API — prompts from config, tools from /admin/tools
  useEffect(() => {
    (async () => {
      try {
        const [configRes, toolsRes] = await Promise.all([
          getAdminConfig().catch(() => ({ config: [] as any[] })),
          getTools().catch(() => ({ tools: [] as AiTool[] })),
        ]);

        const configs = configRes.config || [];
        const now = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

        // System prompt from config
        const sysPrompt = configs.find((c: any) => c.key === 'chat_system_prompt');
        const apiPrompts: PromptRecord[] = sysPrompt ? [{
          id: 'p_system_prompt',
          name: 'chat_system_prompt',
          title: 'Chat system prompt',
          description: 'The main system prompt for the AI chat assistant.',
          status: 'published' as const,
          version: 1,
          updated: sysPrompt.updatedAt ? new Date(sysPrompt.updatedAt).toLocaleString() : now,
          author: 'admin',
          body: String(sysPrompt.value || ''),
          variables: [],
          linkedTools: [],
        }] : [];

        // Tools from /admin/tools API
        const rawTools = toolsRes.tools || [];
        const mappedTools = rawTools.map(t => mapAiToolToToolRecord(t, apiPrompts));

        setPrompts(apiPrompts);
        setTools(mappedTools);

        // Set initial selection
        if (apiPrompts.length > 0) {
          setSelectedId(apiPrompts[0].id);
        } else if (mappedTools.length > 0) {
          setSelectedId(mappedTools[0].id);
          setKind('tool');
        }
      } catch {
        // API unavailable — empty state shown
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const showToast = useCallback((message: string, isError: boolean) => {
    setToast({ visible: true, message, isError });
    setTimeout(() => setToast({ visible: false, message: '', isError: false }), 3000);
  }, []);

  // ── Derived values ───────────────────────────────────────────────────────

  const allItems = [...prompts, ...tools];
  const draftCount = allItems.filter((x) => x.status === 'draft').length;
  const deprecatedCount = allItems.filter((x) => x.status === 'deprecated').length;
  const totalCount = allItems.length;

  const filterStatus = activeFilter === 'drafts' ? 'draft' : activeFilter === 'deprecated' ? 'deprecated' : null;
  const promptItems = filterStatus === null ? prompts : prompts.filter((p) => p.status === filterStatus);
  const toolItems = filterStatus === null ? tools : tools.filter((t) => t.status === filterStatus);

  const selectedItem: PromptRecord | ToolRecord | undefined =
    kind === 'prompt'
      ? prompts.find((p) => p.id === selectedId) || prompts[0]
      : tools.find((t) => t.id === selectedId) || tools[0];

  const selectedTitle = selectedItem ? selectedItem.title : '';
  const selectedName = selectedItem ? selectedItem.name : '';
  const selectedStatus = selectedItem ? selectedItem.status : 'published';
  const selectedVersion = selectedItem ? selectedItem.version : 1;
  const selectedUpdated = selectedItem ? selectedItem.updated : '';
  const selectedAuthor = selectedItem && kind === 'prompt'
    ? (selectedItem as PromptRecord).author
    : selectedItem ? (selectedItem as ToolRecord).owner : '';
  const nextVersion = selectedVersion + 1;
  const publishLabel = 'Publish v' + nextVersion;
  const breadcrumbSection = kind === 'prompt' ? 'Prompts' : 'Tools';
  const kindLabel = kind === 'prompt' ? 'Prompt' : 'Tool';
  const selectedToolType = selectedItem && kind === 'tool' ? (selectedItem as ToolRecord).toolType || '' : '';
  const updatedLine = 'Updated ' + selectedUpdated + ' · by ' + selectedAuthor;
  const canRunTool = kind === 'tool' && selectedItem && !!(selectedItem as ToolRecord).aiToolId;
  const hasEdits = selectedItem ? selectedItem.id in editedValues : false;

  // ── Effects ──────────────────────────────────────────────────────────────

  // Reset playground when selection changes
  useEffect(() => {
    if (!selectedItem) return;
    const defaultInput = kind === 'tool' && (selectedItem as ToolRecord).examples
      ? (selectedItem as ToolRecord).examples[0].in
      : '{\n  "user_name": "Alex",\n  "month": "April 2026",\n  "currency": "CAD"\n}';
    setPlayInput(defaultInput);
    setPlayOutput('');
    setPlayStatus(null);
    setPlayRunning(false);
  }, [selectedId, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleEdit = useCallback((id: string, value: string) => {
    setEditedValues(prev => ({ ...prev, [id]: value }));
  }, []);

  const handlePublish = useCallback(async () => {
    if (!selectedItem) return;
    setPublishing(true);
    try {
      if (kind === 'prompt') {
        const currentValue = editedValues[selectedItem.id] ?? (selectedItem as PromptRecord).body;
        await updateAdminConfig(selectedItem.name, currentValue);
        setPrompts(prev => prev.map(p => p.id === selectedItem.id ? { ...p, body: currentValue, version: p.version + 1 } as PromptRecord : p));
      } else {
        const toolRec = selectedItem as ToolRecord;
        const currentDesc = editedValues[selectedItem.id] ?? toolRec.description;
        const aiToolId = toolRec.aiToolId;
        if (aiToolId) {
          await updateTool(aiToolId, { description: currentDesc });
        } else {
          await updateAdminConfig(selectedItem.name, currentDesc);
        }
        setTools(prev => prev.map(t => t.id === selectedItem.id ? { ...t, description: currentDesc, version: t.version + 1 } as ToolRecord : t));
      }
      setEditedValues(prev => { const next = { ...prev }; delete next[selectedItem.id]; return next; });
      showToast('Published v' + nextVersion, false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Publish failed';
      showToast(msg, true);
    } finally {
      setPublishing(false);
    }
  }, [selectedItem, kind, nextVersion, showToast, editedValues]);

  const handleRun = useCallback(async () => {
    if (!selectedItem || kind !== 'tool') return;
    const toolRec = selectedItem as ToolRecord;
    const aiToolId = toolRec.aiToolId;
    if (!aiToolId) {
      showToast('Cannot run mock tools', true);
      return;
    }
    setPlayRunning(true);
    setPlayOutput('');
    setPlayStatus(null);
    const start = Date.now();
    try {
      const parsed = JSON.parse(playInput);
      const res = await testTool(aiToolId, parsed, 'admin');
      const elapsed = Date.now() - start;
      setPlayOutput(JSON.stringify(res.result, null, 2));
      setPlayStatus({ code: 200, ms: elapsed });
    } catch (err) {
      const elapsed = Date.now() - start;
      const msg = err instanceof Error ? err.message : 'Run failed';
      setPlayOutput(msg);
      setPlayStatus({ code: 500, ms: elapsed });
    } finally {
      setPlayRunning(false);
    }
  }, [selectedItem, kind, playInput, showToast]);

  const handleToolCreated = useCallback((tool: AiTool) => {
    const mapped = mapAiToolToToolRecord(tool, prompts);
    setTools(prev => [...prev, mapped]);
    setKind('tool');
    setSelectedId(mapped.id);
    setCreating(false);
    showToast('Tool created', false);
  }, [prompts, showToast]);

  const handleDeleteTool = useCallback(async () => {
    if (!selectedItem || kind !== 'tool') return;
    const toolRec = selectedItem as ToolRecord;
    const aiToolId = toolRec.aiToolId;
    if (!aiToolId) return;
    if (!window.confirm('Delete tool "' + toolRec.name + '"?')) return;
    try {
      await deleteTool(aiToolId);

      setTools(prev => {
        const next = prev.filter(t => t.id !== selectedItem.id);
        if (next.length > 0) setSelectedId(next[0].id);
        return next;
      });
      showToast('Tool deleted', false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      showToast(msg, true);
    }
  }, [selectedItem, kind, showToast]);

  const handleSaveDraft = useCallback(async () => {
    if (!selectedItem) return;
    setPublishing(true);
    try {
      if (kind === 'prompt') {
        const currentValue = editedValues[selectedItem.id] ?? (selectedItem as PromptRecord).body;
        await updateAdminConfig(selectedItem.name, currentValue);
        setPrompts(prev => prev.map(p => p.id === selectedItem.id ? { ...p, body: currentValue } as PromptRecord : p));
      } else {
        const toolRec = selectedItem as ToolRecord;
        const currentDesc = editedValues[selectedItem.id] ?? toolRec.description;
        const aiToolId = toolRec.aiToolId;
        if (aiToolId) {
          await updateTool(aiToolId, { description: currentDesc, enabled: false });
          setTools(prev => prev.map(t => t.id === selectedItem.id ? { ...t, description: currentDesc, status: 'draft' } as ToolRecord : t));
        } else {
          await updateAdminConfig(selectedItem.name, currentDesc);
          setTools(prev => prev.map(t => t.id === selectedItem.id ? { ...t, description: currentDesc } as ToolRecord : t));
        }
      }
      setEditedValues(prev => { const next = { ...prev }; delete next[selectedItem.id]; return next; });
      showToast('Saved as draft', false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      showToast(msg, true);
    } finally {
      setPublishing(false);
    }
  }, [selectedItem, kind, showToast, editedValues]);

  const handleToggleEnabled = useCallback(async (id: string, enabled: boolean) => {
    const toolRec = tools.find(t => t.id === id);
    if (!toolRec || !toolRec.aiToolId) return;
    try {
      await updateTool(toolRec.aiToolId, { enabled });
      const newStatus = enabled ? 'published' : 'draft';
      setTools(prev => prev.map(t => t.id === id ? { ...t, status: newStatus } as ToolRecord : t));
      const label = enabled ? 'Tool enabled' : 'Tool disabled';
      showToast(label, false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Update failed';
      showToast(msg, true);
    }
  }, [tools, showToast]);

  const handleDiscard = useCallback(() => {
    if (!selectedItem) return;
    setEditedValues(prev => { const next = { ...prev }; delete next[selectedItem.id]; return next; });
    showToast('Changes discarded', false);
  }, [selectedItem, showToast]);

  const handleCopyId = useCallback(() => {
    if (!selectedItem) return;
    const idToCopy = kind === 'tool' && (selectedItem as ToolRecord).aiToolId
      ? String((selectedItem as ToolRecord).aiToolId)
      : selectedItem.id;
    navigator.clipboard.writeText(idToCopy).then(
      () => showToast('ID copied', false),
      () => showToast('Copy failed', true),
    );
  }, [selectedItem, kind, showToast]);

  const handleSelectPrompt = useCallback((id: string) => {
    setKind('prompt');
    setSelectedId(id);
    setCreating(false);
  }, []);

  const handleSelectTool = useCallback((id: string) => {
    setKind('tool');
    setSelectedId(id);
    setCreating(false);
  }, []);

  const handleFilterAll = useCallback(() => setActiveFilter('all'), []);
  const handleFilterDrafts = useCallback(() => setActiveFilter('drafts'), []);
  const handleFilterDeprecated = useCallback(() => setActiveFilter('deprecated'), []);
  const handleTabPlayground = useCallback(() => setRightTab('playground'), []);
  const handleTabHistory = useCallback(() => setRightTab('history'), []);

  // ── Derived styles ───────────────────────────────────────────────────────

  const allChipStyle = activeFilter === 'all' ? { ...s.filterChip, ...s.filterChipActive } : s.filterChip;
  const draftsChipStyle = activeFilter === 'drafts' ? { ...s.filterChip, ...s.filterChipActive } : s.filterChip;
  const deprecatedChipStyle = activeFilter === 'deprecated' ? { ...s.filterChip, ...s.filterChipActive } : s.filterChip;
  const playTabStyle = rightTab === 'playground' ? { ...s.rightTab, ...s.rightTabActive } : s.rightTab;
  const histTabStyle = rightTab === 'history' ? { ...s.rightTab, ...s.rightTabActive } : s.rightTab;

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ ...s.root, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 14, color: AD.SLATE }}>Loading config...</div>
      </div>
    );
  }

  return (
    <div style={s.root}>
      {/* Top bar */}
      <div style={s.top}>
        <div style={s.brand}>
          <div style={s.brandMark}>U</div>
          <div style={s.brandText}>Uplift Admin</div>
        </div>
        <div style={s.crumbs}>
          <span>{breadcrumbSection}</span>
          <ADIcon name="chevron" size={10} color={AD.SLATE_LIGHT} />
          <span style={s.crumbCurrent}>{selectedTitle}</span>
          <span style={{ marginLeft: 8 }}><StatusBadge status={selectedStatus} /></span>
        </div>
        <div style={s.topRight}>
          <div style={s.avatar}>T</div>
        </div>
      </div>

      {/* 3-column body */}
      <div style={s.body}>
        {/* Left rail */}
        <div style={s.left}>
          <div style={s.leftHead}>
            <div style={s.leftTitle}>Library</div>
            <button style={s.leftHeadBtn} aria-label="Add new" onClick={() => { setCreating(true); setKind('tool'); }}>
              <ADIcon name="plus" size={14} color="#fff" />
            </button>
          </div>
          <div style={s.leftFilters}>
            <button style={allChipStyle} onClick={handleFilterAll}>{'All · ' + totalCount}</button>
            <button style={draftsChipStyle} onClick={handleFilterDrafts}>{'Drafts · ' + draftCount}</button>
            <button style={deprecatedChipStyle} onClick={handleFilterDeprecated}>{'Deprecated · ' + deprecatedCount}</button>
          </div>

          <div style={s.leftList}>
            <div style={s.groupLab}>
              <ADIcon name="prompt" size={11} color={AD.SLATE_LIGHT} />
              {'Prompts · ' + promptItems.length}
            </div>
            {promptItems.map((p) => (
              <SidebarItem key={p.id} item={p} selected={kind === 'prompt' && selectedId === p.id} onClick={() => handleSelectPrompt(p.id)} />
            ))}

            <div style={s.groupLab}>
              <ADIcon name="tool" size={11} color={AD.SLATE_LIGHT} />
              {'Tools · ' + toolItems.length}
            </div>
            {toolItems.map((t) => (
              <SidebarItem key={t.id} item={t} selected={kind === 'tool' && selectedId === t.id} onClick={() => handleSelectTool(t.id)} />
            ))}
          </div>
        </div>

        {/* Middle editor */}
        <div style={s.middle}>
          <div style={s.midHead}>
            <div style={s.midTitleCol}>
              <div style={s.midKindRow}>
                <ADIcon name={kind === 'prompt' ? 'prompt' : 'tool'} size={12} color={AD.SLATE} />
                {kindLabel}
                {selectedToolType && (
                  <>
                    <span>·</span>
                    <span style={{ ...s.chip, fontSize: 9, padding: '1px 6px' }}>{selectedToolType}</span>
                  </>
                )}
                <span>·</span>
                <span style={s.midMachine}>{selectedName}</span>
              </div>
              <div style={s.midName}>
                {selectedTitle}
                <StatusBadge status={selectedStatus} />
                <span style={{ fontSize: 13, color: AD.SLATE, fontFamily: 'var(--font-mono, ui-monospace, monospace)' }}>{'v' + selectedVersion}</span>
              </div>
              <div style={{ fontSize: 12, color: AD.SLATE, marginTop: 4 }}>
                {updatedLine}
              </div>
            </div>
            <div style={s.midActions}>
              <button style={s.iconBtn} title="Copy id" aria-label="Copy id" onClick={handleCopyId}>
                <ADIcon name="copy" size={13} color={AD.INK_700} />
              </button>
              {kind === 'tool' && selectedItem && (selectedItem as ToolRecord).aiToolId && (
                <button style={s.iconBtn} title="Delete tool" aria-label="Delete tool" onClick={handleDeleteTool}>
                  <ADIcon name="trash" size={13} color={AD.RED} />
                </button>
              )}
              <button style={s.ghostBtn} onClick={handleDiscard} disabled={!hasEdits}>Discard</button>
              <button style={s.ghostBtn} onClick={handleSaveDraft} disabled={publishing}>Save draft</button>
              <button style={s.primaryBtn} onClick={handlePublish} disabled={publishing} aria-label={publishLabel}>
                <ADIcon name="check" size={12} color="#fff" />
                {publishing ? 'Publishing…' : publishLabel}
              </button>
            </div>
          </div>

          <div style={s.midBody}>
            {creating ? (
              <CreateToolForm
                onCreated={handleToolCreated}
                onCancel={() => setCreating(false)}
                onError={msg => showToast(msg, true)}
              />
            ) : kind === 'prompt' && selectedItem
              ? <PromptEditor key={selectedItem.id} p={selectedItem as PromptRecord} onEdit={handleEdit} />
              : selectedItem
                ? <ToolEditor key={selectedItem.id} tool={selectedItem as ToolRecord} onEdit={handleEdit} onToggleEnabled={handleToggleEnabled} />
                : <div style={{ textAlign: 'center', padding: '60px 20px', color: AD.SLATE }}>
                    <ADIcon name="tool" size={32} color={AD.SLATE_LIGHT} style={{ marginBottom: 12 }} />
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No items yet</div>
                    <div style={{ fontSize: 12 }}>Create a tool with the + button to get started.</div>
                  </div>
            }
          </div>
        </div>

        {/* Right rail */}
        <div style={s.right}>
          <div style={s.rightTabs}>
            <button style={playTabStyle} onClick={handleTabPlayground}>
              <ADIcon name="play" size={12} color={rightTab === 'playground' ? AD.PURPLE_STRONG : AD.SLATE} style={{ verticalAlign: '-2px', marginRight: 6 }} />
              Playground
            </button>
            <button style={histTabStyle} onClick={handleTabHistory}>
              <ADIcon name="history" size={12} color={rightTab === 'history' ? AD.PURPLE_STRONG : AD.SLATE} style={{ verticalAlign: '-2px', marginRight: 6 }} />
              Stats
            </button>
          </div>

          <div style={s.rightBody}>
            {rightTab === 'playground' && kind === 'tool' && (
              <PlaygroundPanel
                kind={kind}
                canRun={canRunTool}
                playInput={playInput}
                playOutput={playOutput}
                playStatus={playStatus}
                playRunning={playRunning}
                onInputChange={setPlayInput}
                onRun={handleRun}
              />
            )}
            {rightTab === 'playground' && kind === 'prompt' && (
              <div style={s.playSec}>
                <div style={{ textAlign: 'center', padding: '24px 0', color: AD.SLATE, fontSize: 12 }}>
                  <ADIcon name="play" size={20} color={AD.SLATE_LIGHT} style={{ marginBottom: 8 }} />
                  <div>Select a tool to use the playground.</div>
                </div>
              </div>
            )}

            {rightTab === 'history' && kind === 'tool' && selectedItem && (
              <ToolStatsPanel key={selectedId} toolName={selectedItem.name} />
            )}
            {rightTab === 'history' && kind === 'prompt' && (
              <div style={s.playSec}>
                <div style={{ textAlign: 'center', padding: '24px 0', color: AD.SLATE, fontSize: 12 }}>
                  <ADIcon name="history" size={20} color={AD.SLATE_LIGHT} style={{ marginBottom: 8 }} />
                  <div>Select a tool to view call stats.</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AdminToast toast={toast} />
    </div>
  );
}

export default AdminPage;
